import type { MantineTheme } from '@mantine/core';
import {
	Box,
	Center,
	createStyles,
	Image,
	useMantineColorScheme,
} from '@mantine/core';
import type { LiteSecodaEntity, LiteSecodaEntityOut } from '@repo/api-codegen';
import { IconEmoji } from '@repo/common/components/IconEmoji/IconEmoji';
import { iconEmojiSelectorValueToString } from '@repo/common/components/IconEmojiSelector/utils';
import {
	DEFAULT_ICONS,
	NOTIFICATION_EVENT_ICON_MAP,
} from '@repo/common/constants';
import { EntityType } from '@repo/common/enums/entityType';
import type { IIntegration } from '@repo/common/models/integration';
import { Icon } from '@repo/foundations';
import type { IconNames } from '@repo/foundations/components/Icon/types';
import { buttonHoverTransition, iconSize } from '@repo/theme/primitives';
import { isNil } from 'lodash-es';
import type { Incident, ISecodaEntity } from '../../../api';
import { useAuthUser, useUser } from '../../../api';
import type { SecodaEntity } from '../../../lib/models';
import { isValidEnumValue } from '../../../utils/enumUtils';
import {
	iconSrcFromIntegration,
	vectorGraphicFromIntegrationType,
} from '../../../utils/integrationLogo';
import IntegrationLogo from '../../IntegrationLogo';
import { SecodaMinimalLogo } from '../../SecodaMinimalLogo';
import { UserAvatar } from '../../UserAvatar';

export type ISecodaEntityIcon = Pick<
	SecodaEntity | ISecodaEntity,
	'entity_type' | 'icon' | 'integration' | 'id' | 'title'
>;

// Helper component to render the actual icon content
function SecodaEntityIconContent({
	entityType,
	icon,
	integration,
	incident,
	notificationEvent,
	emoji,
	user,
	size,
	classes,
	workspace,
	inline = false,
	integrationId,
	isDarkMode = false,
}: {
	entityType?: EntityType | null;
	icon?: string;
	integration?: IIntegration;
	incident?: Incident;
	notificationEvent?: string;
	emoji?: string;
	user?: any;
	size: number;
	classes: any;
	workspace: any;
	inline?: boolean;
	integrationId?: string;
	isDarkMode?: boolean;
}) {
	// Handle integration-specific icons (for inbox notifications)
	if (integration) {
		return (
			<Image
				width={size}
				height={size}
				src={iconSrcFromIntegration(integration, isDarkMode) || 'serverBolt'}
			/>
		);
	}

	// Handle incident-specific icons (for inbox notifications)
	if (incident) {
		return (
			<Image
				width={size}
				height={size}
				src={vectorGraphicFromIntegrationType(
					incident.display_metadata?.target?.integration_type,
					isDarkMode
				)}
			/>
		);
	}

	// Handle emoji icons (for inbox notifications)
	if (emoji) {
		return <IconEmoji value={emoji} size={size} />;
	}

	// Handle notification event icons (for inbox notifications)
	if (notificationEvent) {
		const eventIcon = NOTIFICATION_EVENT_ICON_MAP[notificationEvent];
		if (eventIcon) {
			return <Icon name={eventIcon.icon} color={eventIcon.color} size="md" />;
		}
	}

	// Handle workspace logo fallback (existing functionality)
	if (isNil(entityType)) {
		if (icon) {
			return (
				<Icon name={icon as IconNames} color="icon/primary/default" size="md" />
			);
		}
		return workspace.logo ? (
			<Image width={size} src={workspace.logo} data-testid="workspace-logo" />
		) : (
			<SecodaMinimalLogo
				width={size}
				height={size}
				data-testid="workspace-default-logo"
			/>
		);
	}

	// Handle user avatars (existing functionality with optional user override)
	if (entityType === EntityType.user) {
		const userData = user || {
			id: '',
			disabled: false,
			email: '',
			display_name: 'Untitled',
			profile_picture: '',
			first_name: '',
			last_name: '',
		};

		return <UserAvatar size={size} user={userData} data-testid="user-avatar" />;
	}

	// Handle entity types with emoji support (existing functionality)
	if (
		entityType === EntityType.metric ||
		entityType === EntityType.collection ||
		entityType === EntityType.document ||
		entityType === EntityType.question_reply ||
		entityType === EntityType.question ||
		entityType === EntityType.dictionary_term ||
		entityType === EntityType.glossary ||
		entityType === EntityType.template ||
		entityType === EntityType.policy ||
		entityType === EntityType.bi_dashboard
	) {
		return (
			<Center
				className={classes.center}
				inline={inline}
				data-testid={`${entityType}-icon`}
			>
				<IconEmoji
					value={
						icon ||
						(DEFAULT_ICONS[entityType]
							? iconEmojiSelectorValueToString(DEFAULT_ICONS[entityType]!)
							: '')
					}
					size={size}
				/>
			</Center>
		);
	}

	// Handle integration logos (existing functionality)
	return (
		<Center
			className={classes.center}
			inline={inline}
			data-testid={`integration-${integrationId}-icon`}
		>
			<IntegrationLogo
				size={size - 4}
				entityType={entityType}
				integrationId={integrationId}
			/>
		</Center>
	);
}

interface ISecodaEntityIconProps {
	entity?: ISecodaEntityIcon | LiteSecodaEntity | LiteSecodaEntityOut;
	size?: number;
	inline?: boolean;
	entityType?: EntityType;
	integrationId?: string;
	icon?: string;
	// New props for notification/inbox support
	notificationEvent?: string;
	emoji?: string;
	integration?: IIntegration;
	incident?: Incident;
	user?: {
		id: string;
		email?: string;
		display_name?: string;
		profile_picture?: string | null;
		first_name?: string;
		last_name?: string;
		disabled?: boolean | null;
	};
	// Overlay support for inbox multi-layered icons
	showOverlay?: boolean;
	isIntegration?: boolean;
	isMonitorIncident?: boolean;
	isAutomation?: boolean;
	isAiAgent?: boolean;
	// Hover effect support
	hoverable?: boolean;
}

const useStyles = createStyles(
	(
		theme: MantineTheme,
		{ size, hoverable }: { size: number; hoverable?: boolean }
	) => ({
		center: {
			minWidth: size,
			minHeight: size,
			borderRadius: theme.radius.xl,
		},
		container: {
			alignItems: 'center',
			position: 'relative',
		},
		sourceLogo: {
			position: 'absolute',
			backgroundColor: theme.other.getColor('fill/primary/default'),
			borderRadius: theme.radius.xl,
			bottom: `-${iconSize.sm / 2}px`,
			right: `-${iconSize.sm / 3.5}px`,
		},
		logoWrapper: {
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			width: theme.other.space[7],
			height: theme.other.space[7],
			padding: theme.spacing['3xs'],
			backgroundColor: theme.other.getColor('fill/secondary/default'),
			borderRadius: theme.radius.sm,
			...(hoverable && {
				...buttonHoverTransition('background-color'),
				cursor: 'pointer',
				'&:hover': {
					backgroundColor: theme.other.getColor('fill/primary/hover'),
					borderRadius: theme.radius.sm,
				},
			}),
		},
	})
);

export function SecodaEntityIcon({
	entity,
	inline = false,
	size = 36,
	entityType,
	integrationId,
	icon,
	// New notification/inbox props
	notificationEvent,
	emoji,
	integration,
	incident,
	user,
	showOverlay = false,
	isIntegration = false,
	isMonitorIncident = false,
	isAutomation = false,
	isAiAgent = false,
	hoverable = false,
}: ISecodaEntityIconProps) {
	const { classes } = useStyles({ size, hoverable });
	const { workspace } = useAuthUser();
	const { colorScheme } = useMantineColorScheme();

	const { data: fetchedUser } = useUser({
		id: entity?.id ?? user?.id ?? '',
		options: {
			enabled:
				!!(entity?.id || user?.id) &&
				(entity?.entity_type === EntityType.user || !!user),
		},
	});

	let entityTypeToUse = entityType;

	if (
		!entityTypeToUse &&
		entity?.entity_type &&
		isValidEnumValue(EntityType, entity.entity_type)
	) {
		entityTypeToUse = entity.entity_type;
	}

	const iconToUse = isAiAgent ? 'sparkles' : (entity?.icon ?? icon);
	let integrationIdToUse = integrationId;
	if (entity && 'integration' in entity) {
		if (!integrationIdToUse && typeof entity.integration === 'string') {
			integrationIdToUse = entity.integration;
		} else if (
			!integrationIdToUse &&
			entity.integration &&
			typeof entity.integration === 'object'
		) {
			integrationIdToUse = entity.integration.id;
		}
	}

	// Handle rendering for multi-layered icons
	if (
		showOverlay &&
		(isIntegration || isMonitorIncident || isAutomation || isAiAgent)
	) {
		let overlayIconName: 'refresh' | 'activity' | 'link' | 'topologyStar' =
			'refresh';
		if (isMonitorIncident) {
			overlayIconName = 'activity';
		} else if (isAiAgent) {
			overlayIconName = 'topologyStar';
		} else if (isAutomation) {
			overlayIconName = 'link';
		}

		return (
			<Box className={classes.container}>
				<Box className={classes.logoWrapper}>
					<SecodaEntityIconContent
						entityType={entityTypeToUse}
						icon={iconToUse}
						integration={integration}
						incident={incident}
						notificationEvent={notificationEvent}
						emoji={emoji}
						size={16}
						classes={classes}
						workspace={workspace}
						inline={inline}
						integrationId={integrationIdToUse}
						isDarkMode={colorScheme === 'dark'}
					/>
				</Box>
				<Box className={classes.sourceLogo}>
					<Icon
						name={overlayIconName}
						color={
							isAutomation ? 'icon/info/default' : 'icon/secondary/default'
						}
						size="md"
					/>
				</Box>
			</Box>
		);
	}

	// Handle user avatar with overlay for inbox notifications
	if (showOverlay && (user || entity?.entity_type === EntityType.user)) {
		const userData = fetchedUser || {
			id: user?.id || entity?.id || '',
			disabled: user?.disabled || false,
			email: user?.email || '',
			display_name: user?.display_name || entity?.title || 'Untitled',
			profile_picture: user?.profile_picture || '',
			first_name: user?.first_name || '',
			last_name: user?.last_name || '',
		};

		return (
			<Box className={classes.container}>
				<UserAvatar user={userData} size={28} />
				<Box className={classes.sourceLogo}>
					<SecodaEntityIconContent
						entityType={entityTypeToUse}
						icon={iconToUse}
						integration={integration}
						incident={incident}
						notificationEvent={notificationEvent}
						emoji={emoji}
						size={16}
						classes={classes}
						workspace={workspace}
						inline={inline}
						integrationId={integrationIdToUse}
						isDarkMode={colorScheme === 'dark'}
					/>
				</Box>
			</Box>
		);
	}

	// Standard single icon rendering (backwards compatible)
	return (
		<SecodaEntityIconContent
			entityType={entityTypeToUse}
			icon={iconToUse}
			integration={integration}
			incident={incident}
			notificationEvent={notificationEvent}
			emoji={emoji}
			user={fetchedUser || user}
			size={size}
			classes={classes}
			workspace={workspace}
			inline={inline}
			integrationId={integrationIdToUse}
			isDarkMode={colorScheme === 'dark'}
		/>
	);
}
